import type { CSSProperties } from 'react';

// ─── Shared style tokens for all tablet operation pages ───────────────────────
// Single source of truth: change a value here and every page updates.

export const TABLET_TITLE_STYLE: CSSProperties = { fontSize: 30 };

export const TABLET_BACK_BUTTON_STYLE: CSSProperties = {
	fontSize: 16,
	height: 'auto',
	padding: '8px 20px',
};

export const TABLET_SEARCH_STYLE: CSSProperties = {
	fontSize: 16,
	padding: '8px 12px',
};

export const TABLET_RADIO_GROUP_STYLE: CSSProperties = { fontSize: 16 };

export const TABLET_RADIO_BUTTON_STYLE: CSSProperties = {
	fontSize: 16,
	height: 'auto',
	lineHeight: 1.4,
	padding: '8px 24px',
};

/**
 * For pages that put several filter groups on one row. Same font and touch
 * height as `TABLET_RADIO_BUTTON_STYLE`, narrower horizontal padding, so three
 * groups fit across a 10.1" tablet instead of wrapping onto a second row and
 * eating the list's vertical space.
 */
export const TABLET_FILTER_BUTTON_STYLE: CSSProperties = {
	fontSize: 16,
	height: 'auto',
	lineHeight: 1.4,
	padding: '8px 14px',
};

/**
 * One combined filter row. Replaces stacking a row per filter group: on the
 * 10.1" tablets in the Shop Floor App frame, each extra row costs list space
 * that the operator needs more than the breathing room.
 */
export const TABLET_FILTER_ROW_STYLE: CSSProperties = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexWrap: 'wrap',
	gap: 10,
	margin: '4px 0',
};

export const TABLET_CARD_GRID_STYLE: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 360px))',
	justifyContent: 'center',
	gap: 16,
};

export const TABLET_PRODUCTION_METHOD_ROW_STYLE: CSSProperties = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	margin: '8px 0',
	minHeight: 60,
};

export const TABLET_PRODUCTION_METHOD_GROUP_STYLE: CSSProperties = {
	fontSize: 16,
	display: 'flex',
	alignItems: 'center',
};

export const TABLET_SELECT_TRIGGER_STYLE: CSSProperties = {
	width: '100%',
	height: 50,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 8,
	fontSize: 16,
	padding: '0 12px',
};

export const TABLET_SELECT_OPTION_ROW_STYLE: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	minHeight: 56,
	padding: '8px 12px',
	borderBottom: '1px solid #f0f0f0',
	cursor: 'pointer',
	fontSize: 16,
};

// ─── Operation descriptions shown in the info modals on the home page ─────────

export const OPERATION_DESCRIPTIONS: Record<
	string,
	{ title: string; description: string; usedBy: string }
> = {
	qualityControl: {
		title: 'Quality Control',
		description:
			'Inspect manufactured parts against engineering specifications. Includes hand-measurement (Machine) and automated Keyence optical measurement workflows.',
		usedBy: 'Quality Inspectors',
	},
	cutting: {
		title: 'Cutting (Saw Cut Queue)',
		description:
			'View and manage the queue of material that needs to be cut to size for production orders. Track cut progress, mark bars as cut, and manage material reservations.',
		usedBy: 'Saw Operators',
	},
	receiving: {
		title: 'Receiving',
		description:
			'Log and verify incoming material shipments from vendors. Confirm quantities, inspect material condition, and update inventory records upon receipt.',
		usedBy: 'Warehouse / Receiving Staff',
	},
	kitting: {
		title: 'Kitting',
		description:
			'Prepare kits for the floor. The queue uses the same layout as Cutting: due-date stripes and Ready / In progress / Late views. It opens in scheduler order. Those labels identify a ready, late, hard, or started kit without moving it ahead of the scheduler, and a Material ready chip marks a kit whose material is all cut.',
		usedBy: 'Kitting Operators',
	},
	delivery: {
		title: 'Delivery',
		description:
			'Record finished-part deliveries from the shop floor to shipping. Enter delivered, scrapped, and returned quantities, and complete FOD declarations.',
		usedBy: 'Machine Operators / Shipping Staff',
	},
	material: {
		title: 'Material (Lengths)',
		description:
			'Browse, filter, and manage raw material bar inventory. Move bars between locations, split lengths, modify attributes, and reserve material for jobs.',
		usedBy: 'Warehouse / Material Handlers',
	},
	jawConfiguration: {
		title: 'Jaw Configuration',
		description:
			'Configure multigrip jaw sets at the jaw assembly station. Overlays the selected jaw configuration wireframe on the live camera feed to show exactly where to mount the configurable pads.',
		usedBy: 'Jaw Assembly Station Operators',
	},
	kitStaging: {
		title: 'Kit Tracking',
		description:
			'Track kits and material on the shop floor: job/material staging areas, machine setup readiness, cart routes, returns and dekit. The richer staging view alongside Kitting.',
		usedBy: 'Shop Managers / Staging Operators',
	},
	reservations: {
		title: 'Reservations (Extra Material)',
		description:
			'Machine operators who scrapped or crashed a part request more material from their tablet; those requests land here. Pick a job, and the system offers only bars that match the material the job is already cutting.',
		usedBy: 'Saw Operators / Shop Managers',
	},
};
